import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import apiUrls from "../../../api";
import PlatformIcon from "../../../common/components/Icon/PlatformIcon/PlatformIcon";
import {
  DownloadIcon,
  QuestionMarkCircleIcon,
  ChevronDownIcon,
  InformationCircleIcon,
} from "@heroicons/react/outline";

interface IBuildDownload {
  id: number;
  targetPlatform: string;
  version: string;
  downloadUrl: string;
  sizeInBytes: number | null;
}

interface EnduserDownloadsSectionProps {
  projectGroupId: number;
}

const DOWNLOADS_ACKNOWLEDGED_KEY = "wixar_downloads_acknowledged";

const formatFileSize = (bytes: number | null): string => {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  if (bytes < 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const EnduserDownloadsSection = ({ projectGroupId }: EnduserDownloadsSectionProps) => {
  const { t } = useTranslation();
  const [builds, setBuilds] = useState<IBuildDownload[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAcknowledged, setIsAcknowledged] = useState(() => {
    return localStorage.getItem(DOWNLOADS_ACKNOWLEDGED_KEY) === "true";
  });

  const handleAcknowledge = () => {
    setIsAcknowledged(true);
    localStorage.setItem(DOWNLOADS_ACKNOWLEDGED_KEY, "true");
  };

  useEffect(() => {
    const fetchBuilds = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(
          `${apiUrls.stats.enduserProjectGroupBuilds}?projectGroupId=${projectGroupId}`,
        );
        setBuilds(response.data || []);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch builds:", err);
        setError("Failed to load downloads");
      } finally {
        setIsLoading(false);
      }
    };

    if (projectGroupId) {
      fetchBuilds();
    }
  }, [projectGroupId]);

  // Don't render anything if no builds available
  if (!isLoading && builds.length === 0) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="flex gap-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-10 w-20 bg-gray-100 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
          <DownloadIcon className="h-4 w-4 text-gray-500" />
          {t("enduserStats.downloads.title")}
        </h3>
        <div className="relative group">
          <QuestionMarkCircleIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
          <div className="absolute right-0 top-6 w-64 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
            <p className="mb-2">{t("enduserStats.downloads.helpText")}</p>
            <p className="text-gray-300">{t("enduserStats.downloads.contactAdmin")}</p>
          </div>
        </div>
      </div>

      {!isAcknowledged ? (
        /* Warning state - requires acknowledgment */
        <div className="space-y-3">
          <div className="flex items-start gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <InformationCircleIcon className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-700">
              <p className="font-medium mb-1">{t("enduserStats.downloads.warningTitle")}</p>
              <p className="text-gray-600">{t("enduserStats.downloads.warningText")}</p>
              <p className="text-gray-500 mt-2">{t("enduserStats.downloads.contactAdmin")}</p>
            </div>
          </div>
          <button
            onClick={handleAcknowledge}
            className="flex items-center gap-2 text-sm text-green-600 hover:text-green-700 transition-colors group"
          >
            <ChevronDownIcon className="h-4 w-4 text-green-500 group-hover:text-green-600 transition-transform group-hover:translate-y-0.5" />
            <span>{t("enduserStats.downloads.acknowledge")}</span>
          </button>
        </div>
      ) : (
        /* Downloads revealed */
        <div className="flex flex-wrap gap-2">
          {builds.map((build) => (
            <a
              key={build.id}
              href={build.downloadUrl}
              download
              className="inline-flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-green-50 border border-gray-200 hover:border-green-300 rounded-lg transition-colors group"
              title={`${build.targetPlatform} v${build.version}${
                build.sizeInBytes ? ` (${formatFileSize(build.sizeInBytes)})` : ""
              }`}
            >
              <PlatformIcon
                platform={build.targetPlatform}
                iconProps={{ width: "24px", height: "24px", fill: "#374151" }}
              />
              <span className="text-xs text-gray-600 group-hover:text-green-700">
                v{build.version}
              </span>
              <DownloadIcon className="h-4 w-4 text-gray-400 group-hover:text-green-600" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export default EnduserDownloadsSection;
